import React, { useState, useContext } from 'react';
import {
  Button,
  TextField,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

// Componentes locais
import Header from '../../components/Header';
import IconsBar from '../../components/IconsBar/IconsBar';
import { MultipleClassroomSelect } from '../../components/MultipleClassroomSelect';
import { Loading } from '../../components/Loading/Loading';
import { AlertMessages } from '../../components/AlertMessages';
import { RecipientSelector } from '../../components/IndividualMessages/RecipientSelector';
import { StudentSearch } from '../../components/IndividualMessages/StudentSearch';
import { ResponsableSearch } from '../../components/IndividualMessages/ResponsableSearch';

// API e serviços
import { sendIndividualMessage } from '../../api/endpoints/individualMessages';
import { AuthContext } from '../../contexts/Auth/AuthContext';

// Estilos
import {
  ActionsArea,
  Container,
  ContentArea,
  DividerAreaLeft,
  DividerAreaRight,
  FormItens,
  PageTitle,
} from '../Announcements/styles';

const IndividualMessages = () => {
  const [recipientType, setRecipientType] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedResponsable, setSelectedResponsable] = useState(null);
  const [selectedResponsableStudentId, setSelectedResponsableStudentId] = useState(null);
  const [classrooms, setClassrooms] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [typeMessage, setTypeMessage] = useState('success');
  const [contentMessage, setContentMessage] = useState('');

  const auth = useContext(AuthContext);
  const unit = auth?.user?.unit || 'PATOS';

  const resetFields = () => {
    setRecipientType('');
    setSelectedStudent(null);
    setSelectedResponsable(null);
    setSelectedResponsableStudentId(null);
    setClassrooms([]);
    setTitle('');
    setMessage('');
  };

  const validateForm = () => {
    if (!recipientType) {
      setTypeMessage('error');
      setContentMessage('Selecione o tipo de destinatário');
      setShow(true);
      return false;
    }

    if (recipientType === 'STUDENT' && !selectedStudent) {
      setTypeMessage('error');
      setContentMessage('Selecione um aluno');
      setShow(true);
      return false;
    }

    if (recipientType === 'RESPONSABLE') {
      const responsablesArray = Array.isArray(selectedResponsable) ? selectedResponsable : (selectedResponsable ? [selectedResponsable] : []);
      if (responsablesArray.length === 0) {
        setTypeMessage('error');
        setContentMessage('Selecione pelo menos um responsável');
        setShow(true);
        return false;
      }
    }

    if (recipientType === 'CLASSROOM' && (!classrooms || classrooms.length === 0)) {
      setTypeMessage('error');
      setContentMessage('Selecione pelo menos uma turma');
      setShow(true);
      return false;
    }

    if (!title || !title.trim()) {
      setTypeMessage('error');
      setContentMessage('Informe o título da mensagem');
      setShow(true);
      return false;
    }

    if (!message || !message.trim()) {
      setTypeMessage('error');
      setContentMessage('Informe a mensagem');
      setShow(true);
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const messageData = {
        recipient_type: recipientType,
        title: title.trim(),
        message: message.trim(),
        unit,
      };

      // Limpar campos opcionais antes de adicionar os específicos
      delete messageData.recipient_idSponte;
      delete messageData.classroom_id;

      if (recipientType === 'STUDENT') {
        if (!selectedStudent) {
          setTypeMessage('error');
          setContentMessage('Selecione um aluno');
          setShow(true);
          setLoading(false);
          return;
        }
        
        // Se selectedStudent é string no formato "ID-Nome", extrair o ID
        let studentIdSponte;
        if (typeof selectedStudent === 'string') {
          studentIdSponte = selectedStudent.split('-')[0];
        } else {
          studentIdSponte = selectedStudent.idSponte || selectedStudent.id;
        }
        
        if (!studentIdSponte) {
          setTypeMessage('error');
          setContentMessage('ID do aluno não encontrado');
          setShow(true);
          setLoading(false);
          return;
        }
        
        messageData.recipient_idSponte = String(studentIdSponte);
      } else if (recipientType === 'RESPONSABLE') {
        // Converter para array se não for
        const responsablesArray = Array.isArray(selectedResponsable) ? selectedResponsable : (selectedResponsable ? [selectedResponsable] : []);
        
        if (responsablesArray.length === 0) {
          setTypeMessage('error');
          setContentMessage('Selecione pelo menos um responsável');
          setShow(true);
          setLoading(false);
          return;
        }
        
        // Enviar mensagem para cada responsável selecionado
        const results = [];
        for (const responsable of responsablesArray) {
          let responsableIdSponte;
          if (typeof responsable === 'string') {
            responsableIdSponte = responsable.split('-')[0];
          } else {
            responsableIdSponte = responsable.idSponte || responsable.id;
          }
          
          if (!responsableIdSponte) {
            continue; // Pular se não tiver ID válido
          }
          
          const responsableMessageData = {
            ...messageData,
            recipient_idSponte: String(responsableIdSponte),
            student_idSponte: selectedResponsableStudentId || undefined,
          };
          
          try {
            const result = await sendIndividualMessage(responsableMessageData);
            results.push(result);
          } catch (error) {
            console.error(`Erro ao enviar mensagem para responsável ${responsableIdSponte}:`, error);
            results.push({ status: false, error: error.message });
          }
        }
        
        // Consolidar resultados
        const total = results.reduce((sum, r) => sum + (r.total || 0), 0);
        const successful = results.reduce((sum, r) => sum + (r.successful || 0), 0);
        const failed = results.reduce((sum, r) => sum + (r.failed || 0), 0);
        
        setTypeMessage('success');
        let message = `Mensagem enviada com sucesso!`;
        if (total > 0) {
          message += `\nTotal de destinatários: ${total}`;
          message += `\nEnviados: ${successful}`;
          if (failed > 0) {
            message += `\nFalhas: ${failed}`;
          }
        } else {
          message = `Mensagem enviada para ${successful} destinatário(s)!`;
        }
        
        setContentMessage(message);
        setShow(true);
        resetFields();
        setLoading(false);
        return;
        
      } else if (recipientType === 'CLASSROOM') {
        // Se for turma, enviar para cada turma selecionada
        // O backend vai processar e enviar para todos os alunos e responsáveis
        if (classrooms.length === 0) {
          setTypeMessage('error');
          setContentMessage('Selecione pelo menos uma turma');
          setShow(true);
          setLoading(false);
          return;
        }
        // Converter para número (o backend espera number)
        const classroomId = typeof classrooms[0] === 'string' 
          ? parseInt(classrooms[0], 10) 
          : Number(classrooms[0]);
        
        if (isNaN(classroomId)) {
          setTypeMessage('error');
          setContentMessage('ID da turma inválido');
          setShow(true);
          setLoading(false);
          return;
        }
        
        messageData.classroom_id = classroomId;
      }

      const result = await sendIndividualMessage(messageData);

      if (result.status || result.successful > 0) {
        setTypeMessage('success');
        const total = result.total || 0;
        const successful = result.successful || 0;
        const failed = result.failed || 0;
        
        let message = `Mensagem enviada com sucesso!`;
        if (total > 0) {
          message += `\nTotal de destinatários: ${total}`;
          message += `\nEnviados: ${successful}`;
          if (failed > 0) {
            message += `\nFalhas: ${failed}`;
          }
        } else {
          message = `Mensagem enviada para ${successful} destinatário(s)!`;
        }
        
        setContentMessage(message);
        setShow(true);
        resetFields();
      } else {
        throw new Error('Erro ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setTypeMessage('error');
      
      // Extrair mensagem de erro mais detalhada
      let errorMessage = 'Erro ao enviar mensagem. Tente novamente.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Se tiver detalhes de validação, mostrar eles
        if (errorData.details?.validationErrors) {
          const validationErrors = errorData.details.validationErrors;
          errorMessage = validationErrors.map(err => `${err.field}: ${err.message}`).join('\n');
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setContentMessage(errorMessage);
      setShow(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header />
      <IconsBar />
      <ContentArea>
        <PageTitle>Enviar Mensagem Individual</PageTitle>

        <FormItens>
          <RecipientSelector
            value={recipientType}
            onChange={(e) => {
              setRecipientType(e.target.value);
              // Limpar seleções quando mudar o tipo
              setSelectedStudent(null);
              setSelectedResponsable(null);
              setSelectedResponsableStudentId(null);
              setClassrooms([]);
            }}
          />

          {recipientType === 'STUDENT' && (
            <StudentSearch
              value={selectedStudent}
              onChange={setSelectedStudent}
            />
          )}

          {recipientType === 'RESPONSABLE' && (
            <ResponsableSearch
              value={selectedResponsable}
              onChange={setSelectedResponsable}
              onStudentChange={setSelectedResponsableStudentId}
            />
          )}

          {recipientType === 'CLASSROOM' && (
            <MultipleClassroomSelect
              classrooms={classrooms}
              setClassrooms={setClassrooms}
            />
          )}

          <TextField
            label="Título da Mensagem"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            variant="outlined"
          />

          <TextField
            label="Mensagem"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={6}
            variant="outlined"
            placeholder="Digite a mensagem que será enviada como notificação push..."
          />

          <ActionsArea>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={loading}
              sx={{
                minWidth: 200,
                height: 48,
                fontSize: '16px',
                fontWeight: 600,
              }}
            >
              {loading ? 'Enviando...' : 'Enviar Mensagem'}
            </Button>
          </ActionsArea>
        </FormItens>
      </ContentArea>

      <AlertMessages
        show={show}
        setShow={setShow}
        typeMessage={typeMessage}
        contentMessage={contentMessage}
      />

      {loading && <Loading />}
    </Container>
  );
};

export default IndividualMessages;

